/**
 * HOTFIX PR-36H.2B — Entertainment Hub Manual Activation + Performance Lock.
 *
 * Pure, demo-safe module. Owns the user setting that decides whether the
 * Entertainment Hub (a.k.a. Halftime Engagement Hub) is allowed to open
 * automatically and exposes a single evaluator that every auto-open caller
 * MUST consult before opening. Zero Overlap & MODO DEMO intact.
 */
import { trackEvent } from "@/lib/audit";

export type EntertainmentHubMode =
  | "manual_only"
  | "halftime_only"
  | "smart_optional";

export const ENTERTAINMENT_HUB_MODE_KEY = "vs_entertainment_hub_mode";
export const DEFAULT_ENTERTAINMENT_HUB_MODE: EntertainmentHubMode = "manual_only";

/** Reasons an auto-open may be requested. Matches HubTrigger surface. */
export type AutoOpenReason =
  | "halftime"
  | "commercial_break"
  | "post_match"
  | "goal"
  | "post_poll"
  | "post_reward"
  | "ai_director"
  | "social_gravity"
  | "manual";

const SAFE_HALFTIME_REASONS = new Set<AutoOpenReason>([
  "halftime",
  "commercial_break",
  "post_match",
]);

const FORBIDDEN_AUTO_REASONS = new Set<AutoOpenReason>([
  "goal",
  "post_poll",
  "post_reward",
  "ai_director",
  "social_gravity",
]);

let cachedMode: EntertainmentHubMode | null = null;
const listeners = new Set<(mode: EntertainmentHubMode) => void>();

export function readHubMode(): EntertainmentHubMode {
  if (cachedMode) return cachedMode;
  if (typeof window === "undefined") return DEFAULT_ENTERTAINMENT_HUB_MODE;
  try {
    const raw = window.localStorage.getItem(ENTERTAINMENT_HUB_MODE_KEY);
    if (raw === "manual_only" || raw === "halftime_only" || raw === "smart_optional") {
      cachedMode = raw;
      return raw;
    }
  } catch { /* noop */ }
  cachedMode = DEFAULT_ENTERTAINMENT_HUB_MODE;
  return cachedMode;
}

export function setHubMode(mode: EntertainmentHubMode): void {
  cachedMode = mode;
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(ENTERTAINMENT_HUB_MODE_KEY, mode); } catch { /* noop */ }
  }
  listeners.forEach((fn) => { try { fn(mode); } catch { /* noop */ } });
  void trackEvent("entertainment_hub_mode_changed", { mode });
}

export function onHubModeChange(fn: (mode: EntertainmentHubMode) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export interface AutoOpenEvaluation {
  allow: boolean;
  reason: string;
  mode: EntertainmentHubMode;
  suggestion: boolean;
}

/**
 * Single gate every Entertainment Hub auto-open caller must consult.
 * Manual opens (FAB / header button / explicit user click) MUST NOT call this.
 */
export function evaluateAutoOpen(reason: AutoOpenReason): AutoOpenEvaluation {
  const mode = readHubMode();
  if (reason === "manual") {
    return { allow: true, reason: "Apertura manual.", mode, suggestion: false };
  }
  if (FORBIDDEN_AUTO_REASONS.has(reason)) {
    void trackEvent("entertainment_hub_auto_open_blocked", { reason, mode, cause: "forbidden_source" });
    return { allow: false, reason: "Origen prohibido — solo sugerencia discreta.", mode, suggestion: true };
  }
  if (mode === "manual_only") {
    void trackEvent("entertainment_hub_auto_open_blocked", { reason, mode, cause: "manual_only" });
    return { allow: false, reason: "Modo Manual Only — sin auto-apertura.", mode, suggestion: false };
  }
  if (mode === "halftime_only") {
    const allow = SAFE_HALFTIME_REASONS.has(reason);
    if (!allow) {
      void trackEvent("entertainment_hub_auto_open_blocked", { reason, mode, cause: "non_halftime" });
    }
    return {
      allow,
      reason: allow ? "Halftime permitido." : "Solo halftime/commercial/post-match.",
      mode,
      suggestion: !allow,
    };
  }
  // smart_optional — allow halftime family always, otherwise suggestion-only.
  if (SAFE_HALFTIME_REASONS.has(reason)) {
    return { allow: true, reason: "Smart: halftime permitido.", mode, suggestion: false };
  }
  void trackEvent("entertainment_hub_auto_open_blocked", { reason, mode, cause: "smart_suggestion" });
  return { allow: false, reason: "Smart: convertido en sugerencia discreta.", mode, suggestion: true };
}

// ---------- Background limiter ----------

export interface BackgroundLimiterPlan {
  pausePolling: boolean;
  pauseAnimations: boolean;
  pauseListeners: boolean;
  reason: string;
}

/**
 * When the Hub is closed, recommend a background limiter plan so callers
 * pause polling, reactive renders, animations and listeners scoped to the
 * hub. The Hub component itself unmounts its panels when closed; this is
 * the contract for any subscriber outside the hub tree.
 */
export function buildBackgroundLimiterPlan(hubOpen: boolean): BackgroundLimiterPlan {
  if (hubOpen) {
    return { pausePolling: false, pauseAnimations: false, pauseListeners: false, reason: "Hub abierto." };
  }
  return {
    pausePolling: true,
    pauseAnimations: true,
    pauseListeners: true,
    reason: "Hub cerrado — sin actividad en background.",
  };
}

// ---------- Mobile compact ----------

export const MOBILE_COMPACT_MAX_TABS = 4;

export interface MobileCompactPlan {
  visibleTabs: string[];
  carousel: boolean;
  hiddenCount: number;
}

/** Pick the highest-priority tabs for the mobile compact carousel. */
export function buildMobileCompactPlan(allTabs: string[], priority: string[]): MobileCompactPlan {
  const sorted = [...allTabs].sort((a, b) => {
    const ia = priority.indexOf(a); const ib = priority.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  const visible = sorted.slice(0, MOBILE_COMPACT_MAX_TABS);
  return { visibleTabs: visible, carousel: true, hiddenCount: Math.max(0, allTabs.length - visible.length) };
}

// ---------- AI Director restriction ----------

export interface DirectorHubSuggestion {
  open: boolean;
  suggest: boolean;
  caption: string;
}

/**
 * Convert any AI Director request to interact with the Entertainment Hub
 * into a discrete suggestion. The Director can NEVER auto-open the hub.
 */
export function directorHubAction(intent: "open" | "suggest" | "ignore"): DirectorHubSuggestion {
  if (intent === "ignore") return { open: false, suggest: false, caption: "Sin acción." };
  const plan: DirectorHubSuggestion = {
    open: false,
    suggest: true,
    caption: "Sugerencia discreta — el usuario decide.",
  };
  void trackEvent("entertainment_hub_director_suggestion", { intent });
  return plan;
}
